"use client";

import type { CSSProperties } from "react";
import {
  Button,
  Icon,
  P2,
  P3,
  PopoverTrigger,
  Skeleton,
  Tag,
  theme,
} from "@focal/ui-kit";
import { useRouter } from "next/navigation";
import AvatarComponent from "@/components/common/AvatarCustom/AvatarCustom";
import { ANALYTICS_EVENTS, routes } from "@/constants";
import { useAnalytics } from "@/lib";
import {
  getParticipantEmail,
  getParticipantName,
  getParticipantRoleLabel,
} from "@/lib/participant-utils";
import { cn } from "@/lib/utils";
import type { Participant } from "@/types";

interface ConversationPersonCardProps {
  className?: string;
  participant?: Partial<Participant>;
  onDeleteParticipant?: (participantId: string) => void;
  disableRedirect?: boolean;
  disableRemove?: boolean;
  disableUsers?: boolean;
  isLoading?: boolean;
  showAssignmentStatus?: boolean;
  removeLabel?: string;
  pickerTrigger?: boolean;
}

export const ConversationPersonCard = ({
  className,
  participant,
  onDeleteParticipant,
  disableRedirect,
  disableRemove,
  disableUsers,
  isLoading = false,
  showAssignmentStatus = false,
  removeLabel,
  pickerTrigger = false,
}: ConversationPersonCardProps) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const name = getParticipantName(participant);
  const email = getParticipantEmail(participant);
  const role = getParticipantRoleLabel(participant);
  const linked = !!(participant?.clientId || participant?.userId);
  const canRemove =
    !disableRemove && !!participant?.id && !!onDeleteParticipant;
  const actionLabel =
    removeLabel ?? (linked ? "Unassign participant" : "Remove participant");

  if (!participant) {
    return (
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={disableUsers ? "sm" : "icon"}
          aria-label="Add participant"
          data-cy="add-participant-button"
        >
          <Icon name="AddIcon" size="md" />
          {disableUsers && "Add participant"}
        </Button>
      </PopoverTrigger>
    );
  }

  const bodyClass = cn(
    "flex h-[68px] w-full min-w-0 items-center gap-2 rounded-xl border p-2 text-left transition-colors",
    linked
      ? "border-border group-hover/participant:bg-info-foreground"
      : "border-dashed border-[color:var(--assignment-border)] bg-warning-foreground group-hover/participant:bg-warning-foreground/50",
    className,
  );
  const content = isLoading ? (
    <Skeleton className="h-10 w-full" />
  ) : (
    <>
      {linked && (
        <AvatarComponent
          name={name}
          className="h-6 w-6 shrink-0 rounded-full text-xs"
        />
      )}
      <div className="min-w-0 flex-1">
        <P2 className="truncate font-medium">
          {linked ? name : "Click to assign"}
        </P2>
        <P3 textColor="secondary" className="truncate">
          {linked ? email : name || email}
        </P3>
      </div>
      {(role || !linked || showAssignmentStatus) && (
        <div className="flex shrink-0 flex-col items-start gap-0.5">
          {role && (
            <Tag variant="blue" size="xs" data-cy="participant-role">
              {role}
            </Tag>
          )}
          {(!linked || showAssignmentStatus) && (
            <Tag
              variant={linked && participant.speakerId ? "green" : "orange"}
              size="xs"
              className="px-2"
              data-cy={!linked ? "participant-unassigned" : undefined}
            >
              {linked && participant.speakerId ? "Assigned" : "Unassigned"}
            </Tag>
          )}
        </div>
      )}
    </>
  );
  const canNavigate = linked && !!participant.clientId && !disableRedirect;
  const navigate = () => {
    if (canNavigate && participant.clientId)
      router.push(routes.clients.details(participant.clientId));
  };

  return (
    <div
      className="group/participant relative min-w-0 w-full overflow-hidden rounded-xl"
      style={
        { "--assignment-border": theme.borders.color.warning } as CSSProperties
      }
    >
      {!linked || pickerTrigger ? (
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={isLoading}
            aria-label={
              linked ? "Edit assigned participant" : "Assign participant"
            }
            data-cy={!linked ? "add-detected-participant" : undefined}
            className={cn(
              bodyClass,
              "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-wait",
            )}
          >
            {content}
          </button>
        </PopoverTrigger>
      ) : (
        <div
          className={cn(
            bodyClass,
            canNavigate &&
              "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          )}
          onClick={navigate}
          role={canNavigate ? "button" : undefined}
          tabIndex={canNavigate ? 0 : undefined}
          onKeyDown={(event) => {
            if (canNavigate && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              navigate();
            }
          }}
        >
          {content}
        </div>
      )}
      {canRemove && (
        <div
          className={cn(
            "absolute right-px top-px bottom-px flex translate-x-[calc(100%+1px)] items-center rounded-r-[11px] px-3 transition-transform group-hover/participant:translate-x-0 group-focus-within/participant:translate-x-0",
            linked
              ? "bg-[#F1F5F9] shadow-[rgba(245,245,245,0.7)_0_5px_4px_20px]"
              : "bg-warning-foreground",
          )}
        >
          <Button
            variant="ghostSecondary"
            size="icon"
            aria-label={actionLabel}
            data-cy="delete-participant"
            disabled={isLoading}
            onClick={(event) => {
              event.stopPropagation();
              track(ANALYTICS_EVENTS.CONVERSATION_DETAILS_PARTICIPANT_REMOVED, {
                event_text: actionLabel,
              });
              onDeleteParticipant?.(participant.id!);
            }}
          >
            <Icon name="CloseIcon" size="lg" />
          </Button>
        </div>
      )}
    </div>
  );
};
